import { Component, ComponentFactoryResolver, ComponentRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { HsgModal } from '@hhsg/hsg-asc';

import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';

import { AnotherUserModalComponent } from '../../another-user-modal/another-user-modal.component';
import { PageDirective } from './page.directive';
import { PageInterface } from './page.interface';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowPlannerService } from 'src/shared/services/workflow-planner.service';

@Component({
    selector: 'smt-staffing-plan-page',
    templateUrl: './page.component.html',
    styleUrls: [ './page.component.scss' ]
})
export class PageComponent implements OnChanges, OnInit {
    @Input() currentPage: WorkflowPage;
    
    @Output() doneButtonClicked = new EventEmitter();
    @Output() nextButtonClicked = new EventEmitter();
    @Output() skipButtonClicked = new EventEmitter();
    @Output() previousButtonClicked = new EventEmitter();

    private _unsubscribe$ = new Subject();

    private instance: PageInterface;
    isFormValid = false;
    loading = false;
    error = false;
    
    @ViewChild(PageDirective, {static:true}) pageHost: PageDirective;

    componentRef: ComponentRef<any>;

    constructor(private componentFactoryResolver: ComponentFactoryResolver, private _workflowPlannerService: WorkflowPlannerService,  private modal: HsgModal) { 

    }

    async ngOnChanges() {
        await this.loadComponent();

        if (this.instance.formGroups) {
            this.instance.formGroups.filter(f => f).forEach(f => { 
                f.valueChanges.pipe(
                    // This is a workaround to this angular bug: https://github.com/angular/angular/issues/12540
                    // Without this, if you only have number inputs, it will continuously spam isFormValid()
                    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
                ).subscribe(() => { 
                    setTimeout(() => this._workflowPlannerService.announceValidation(this.instance.isFormValid())); 
                }); 
            });
        }        

        this._workflowPlannerService.announceValidation(this.instance.isFormValid());
    }

    ngOnInit() {
        if (!this.currentPage) {
            return;
        }

        this._workflowPlannerService.formValidation$
                                    .pipe(takeUntil(this._unsubscribe$))
                                    .subscribe((valid: boolean) => {
                                        this.isFormValid = valid;
                                    });
    }

    async done() {
        this.currentPage.shiftRecord.status = ShiftRecordStatus.Done;        
        let shiftRecordId = await this.save();

        if (shiftRecordId > 0) {
            this.doneButtonClicked.emit();
        }
    }

    async loadComponent() {  
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.currentPage.component);
    
        const viewContainerRef = this.pageHost.viewContainerRef;
        viewContainerRef.clear();
    
        this.componentRef = viewContainerRef.createComponent(componentFactory);

        this.instance = (<PageInterface>this.componentRef.instance);

        this.instance.page = this.currentPage;          

        this.instance.previousRequested.subscribe(() => this.previous());
    }

    async next() {
        let skipSave = this.instance.shouldSkipSave();

        if (skipSave) {            
            this.skip();
        }
        else {
            let shiftRecordId = await this.save();
        
            if (shiftRecordId > 0) {
                this.nextButtonClicked.emit(shiftRecordId);
            } 
        }               
    }

    previous() {
        this.previousButtonClicked.emit();
    }
    
    private async save(): Promise<number> {
        this.loading = true;
        
        let shiftRecordId = await this.instance.save();
        if (!shiftRecordId) {
            this.error = true;
        } else {
            this.error = false;
        }

        // If the ShiftRecordId is -1, it means that someone else has saved the same shift record before this request.
        if (shiftRecordId < 0) {
            this.openAnotherUserSavedModal();
        } else {
            this.loading = false;
        }        

        return shiftRecordId;
    }

    private shouldSkipSave(): boolean {
        this.loading = true;

        let skipSave = this.instance.shouldSkipSave();
        
        this.loading = false;

        return skipSave;
    }
    
    async skip() {
        this.skipButtonClicked.emit();  
    }

    openAnotherUserSavedModal() {
        this.modal.open(AnotherUserModalComponent, {
            data: {
                size: { width: '400px', height: '500px' }
            }
        });
    }
}
